import { useCallback, useRef, useState } from 'react'

const useInlineTableEdit = ({
  onPersist,
  getRecordId = record => record?._id,
}) => {
  const [inlineEditCell, setInlineEditCell] = useState(null)
  const [inlineEditValue, setInlineEditValue] = useState('')
  const [inlineEditError, setInlineEditError] = useState('')
  const inlineCommitRef = useRef(false)

  const isActive = useCallback(
    (record, field) =>
      inlineEditCell?.recordId === getRecordId(record) &&
      inlineEditCell?.field === field,
    [getRecordId, inlineEditCell],
  )

  const cancelInlineEdit = useCallback(() => {
    setInlineEditCell(null)
    setInlineEditValue('')
    setInlineEditError('')
    inlineCommitRef.current = false
  }, [])

  const startInlineEdit = useCallback(
    (record, field, initialValue) => {
      setInlineEditCell({
        recordId: getRecordId(record),
        field,
        record,
      })
      setInlineEditValue(initialValue ?? '')
      setInlineEditError('')
      inlineCommitRef.current = false
    },
    [getRecordId],
  )

  const commitInlineEdit = useCallback(async () => {
    if (!inlineEditCell || inlineCommitRef.current) {
      return
    }

    inlineCommitRef.current = true
    setInlineEditError('')

    try {
      const result = await onPersist({
        record: inlineEditCell.record,
        field: inlineEditCell.field,
        rawValue: inlineEditValue,
      })

      if (result?.error) {
        setInlineEditError(result.error)
        return
      }

      if (result?.ok) {
        cancelInlineEdit()
      }
    } catch (error) {
      console.error('Failed to save inline edit', error)
      setInlineEditError('Unable to save right now. Please try again.')
    } finally {
      inlineCommitRef.current = false
    }
  }, [cancelInlineEdit, inlineEditCell, inlineEditValue, onPersist])

  const onInlineInputKeyDown = useCallback(
    event => {
      if (event.key === 'Enter') {
        event.preventDefault()
        commitInlineEdit()
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        cancelInlineEdit()
      }
    },
    [cancelInlineEdit, commitInlineEdit],
  )

  return {
    inlineEditValue,
    inlineEditError,
    isActive,
    startInlineEdit,
    commitInlineEdit,
    onInlineInputKeyDown,
    setInlineEditValue,
  }
}

export default useInlineTableEdit
